// https://usehooks-ts.com/react-hook/use-media-query
import { useState } from "react";
import { useIsomorphicLayoutEffect } from "@/lib/hooks/use-isomorphic-layout-effect";

type UseMediaQueryOptions = {
  defaultValue?: boolean;
  initializeWithValue?: boolean;
  isActive?: boolean;
};

const IS_SERVER = typeof window === "undefined";

export const useMediaQuery = (
  query: string,
  {
    defaultValue = false,
    initializeWithValue = true,
    isActive = true,
  }: UseMediaQueryOptions = {},
): boolean => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [matches, setMatches] = useState<boolean>(() => {
    if (!initializeWithValue || IS_SERVER) {
      return defaultValue;
    }

    return window.matchMedia(query).matches;
  });

  useIsomorphicLayoutEffect(() => {
    if (!isActive) {
      return;
    }

    setIsHydrated(true);
    const matchMedia = window.matchMedia(query);

    // Triggered at the first client-side load and if query changes
    const handleChange = () => setMatches(matchMedia.matches);
    handleChange();

    // Use deprecated `addListener` and `removeListener` to support Safari < 14
    if (matchMedia.addListener) {
      matchMedia.addListener(handleChange);
      return () => matchMedia.removeListener(handleChange);
    } else {
      matchMedia.addEventListener("change", handleChange);
      return () => matchMedia.removeEventListener("change", handleChange);
    }
  }, [query, isActive]);

  return isHydrated ? matches : defaultValue;
};
